import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDirectionByLanguage } from '../../../hooks/useDirectionByLanguage';
import '../ClientDetails.css';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const ClientActionCard = ({
    icon,
    title,
    items,
    subItemNumber = false,
    disabled = false,
    directClick = false,
    onDirectClick = null,
    variant = 'default',
    aiLabel = 'AI',
    accent = 'blue',
}) => {
    const isAi = variant === 'ai';
    const accentClass = isAi ? '' : ` client-action-card-wrap--${accent}`;
    const [isOpen, setIsOpen] = useState(false);
    const [flyoutIndex, setFlyoutIndex] = useState(null);
    const [flyoutTop, setFlyoutTop] = useState(0);
    const wrapRef = useRef(null);
    const menuRef = useRef(null);
    const itemRefs = useRef({});
    const closeTimerRef = useRef(null);
    const { i18n } = useTranslation();
    const direction = useDirectionByLanguage(i18n);
    const isRtl = direction === 'rtl';
    const FlyoutChevron = isRtl ? ChevronLeftIcon : ChevronRightIcon;

    const clearCloseTimer = () => {
        if (closeTimerRef.current != null) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    useEffect(() => () => clearCloseTimer(), []);

    useEffect(() => {
        if (!isOpen) {
            setFlyoutIndex(null);
            return undefined;
        }

        const handleClickOutside = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) {
                clearCloseTimer();
                setIsOpen(false);
                setFlyoutIndex(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    // Keep the side flyout top-aligned with the row that opened it.
    useLayoutEffect(() => {
        if (flyoutIndex == null || !menuRef.current) {
            setFlyoutTop(0);
            return undefined;
        }

        const syncFlyoutTop = () => {
            const menuEl = menuRef.current;
            const itemEl = itemRefs.current[flyoutIndex];
            if (!menuEl || !itemEl) return;

            const menuRect = menuEl.getBoundingClientRect();
            const itemRect = itemEl.getBoundingClientRect();
            setFlyoutTop(Math.max(0, itemRect.top - menuRect.top));
        };

        syncFlyoutTop();

        const mainEl = menuRef.current.querySelector('.client-action-card-menu-main');
        mainEl?.addEventListener('scroll', syncFlyoutTop, { passive: true });
        window.addEventListener('resize', syncFlyoutTop);

        return () => {
            mainEl?.removeEventListener('scroll', syncFlyoutTop);
            window.removeEventListener('resize', syncFlyoutTop);
        };
    }, [flyoutIndex, isOpen, items]);

    const openMenu = () => {
        if (disabled || directClick) return;
        clearCloseTimer();
        setIsOpen(true);
    };

    const closeMenu = () => {
        if (disabled || directClick) return;
        // Small delay so the pointer can cross into the side flyout without
        // the parent menu collapsing mid-move.
        clearCloseTimer();
        closeTimerRef.current = window.setTimeout(() => {
            setIsOpen(false);
            setFlyoutIndex(null);
            closeTimerRef.current = null;
        }, 160);
    };

    const toggleMenu = () => {
        if (disabled || directClick) return;
        clearCloseTimer();
        setIsOpen((prev) => {
            if (prev) setFlyoutIndex(null);
            return !prev;
        });
    };

    const flyoutItem =
        flyoutIndex != null && items?.[flyoutIndex]?.subItems
            ? items[flyoutIndex]
            : null;

    return (
        <div
            ref={wrapRef}
            className={`client-action-card-wrap ${direction}${isAi ? ' client-action-card-wrap--ai' : ''}${accentClass}`}
            onMouseEnter={openMenu}
            onMouseLeave={closeMenu}
        >
            <div
                className={`client-action-card ${direction} ${disabled ? 'disabled' : ''} ${isOpen ? 'is-open' : ''}${isAi ? ' client-action-card--ai' : ` client-action-card--${accent}`}`}
                onClick={() => {
                    if (disabled) return;
                    if (directClick && onDirectClick) {
                        onDirectClick();
                        return;
                    }
                    toggleMenu();
                }}
                role="button"
                aria-expanded={!directClick ? isOpen : undefined}
                aria-haspopup={!directClick ? 'true' : undefined}
            >
                {isAi ? (
                    <div className="client-action-card-ai-row">
                        <span className="client-action-card-icon-container client-action-card-icon-container--ai">
                            {typeof icon === 'string' ? (
                                <img src={icon} alt="" />
                            ) : (
                                icon
                            )}
                            <span className="client-action-card-ai-icon-badge">{aiLabel}</span>
                        </span>
                        <div className="client-action-card-title" title={`${title} ${aiLabel}`}>
                            {title}
                        </div>
                        <span className="client-action-card-ai-badge">{aiLabel}</span>
                    </div>
                ) : (
                    <>
                        <div className="client-action-card-icon-container">
                            {typeof icon === 'string' ? (
                                <img src={icon} alt="" />
                            ) : (
                                icon
                            )}
                        </div>
                        <div className="client-action-card-title" title={title}>
                            {title}
                        </div>
                    </>
                )}

                {!directClick && (
                    <div className={`client-action-card-icon-arrow ${isOpen ? 'is-open' : ''}`} aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M8.12461 9.00002L12.0046 12.88L15.8846 9.00002C16.2746 8.61002 16.9046 8.61002 17.2946 9.00002C17.6846 9.39002 17.6846 10.02 17.2946 10.41L12.7046 15C12.3146 15.39 11.6846 15.39 11.2946 15L6.70461 10.41C6.31461 10.02 6.31461 9.39002 6.70461 9.00002C7.09461 8.62002 7.73461 8.61002 8.12461 9.00002Z"
                                fill="currentColor"
                            />
                        </svg>
                    </div>
                )}

                <span className="client-action-card-line" aria-hidden="true" />
            </div>

            {isAi && (
                <div className="client-action-card-ai-glow-corner" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 10 10" fill="none">
                        <path d="M5 0.5L5.6 2.4L7.5 3L5.6 3.6L5 5.5L4.4 3.6L2.5 3L4.4 2.4Z" fill="#F9AA2A" />
                    </svg>
                </div>
            )}

            {isOpen && !disabled && !directClick && (
                <div
                    ref={menuRef}
                    className={`client-action-card-menu ${direction}${flyoutItem ? ' has-flyout' : ''}`}
                >
                    <div className="client-action-card-menu-main">
                        {items?.map((item, index) => {
                            const hasSubItems = item?.subItems && Array.isArray(item.subItems);
                            const isFlyoutOpen = flyoutIndex === index;

                            return (
                                <div
                                    key={index}
                                    ref={(node) => {
                                        if (node) itemRefs.current[index] = node;
                                        else delete itemRefs.current[index];
                                    }}
                                    className={`client-action-card-menu-item${isFlyoutOpen ? ' is-flyout-open' : ''}`}
                                    onMouseEnter={() => {
                                        if (hasSubItems) setFlyoutIndex(index);
                                        else setFlyoutIndex(null);
                                    }}
                                    onClick={() => {
                                        if (hasSubItems) {
                                            setFlyoutIndex((prev) => (prev === index ? null : index));
                                            return;
                                        }
                                        item.onClick?.();
                                        setIsOpen(false);
                                        setFlyoutIndex(null);
                                    }}
                                >
                                    <div className="client-action-card-menu-item-row">
                                        <div className="client-action-card-menu-item-main">
                                            {typeof item.icon === 'string' ? (
                                                <img src={item.icon} alt="" className="item-icon" />
                                            ) : (
                                                <span className="item-icon">
                                                    {React.isValidElement(item.icon) ? item.icon : <item.icon />}
                                                </span>
                                            )}
                                            <span className="item-label">{item.label}</span>
                                        </div>
                                        {item.count !== null && item.count !== undefined && (
                                            <div className="item-notification-count">{item.count}</div>
                                        )}
                                        {hasSubItems && (
                                            <span className="expand-arrow" aria-hidden="true">
                                                <FlyoutChevron fontSize="small" />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {flyoutItem && (
                        <div
                            className={`client-action-card-flyout ${direction}`}
                            style={{ top: flyoutTop }}
                            onMouseEnter={() => {
                                clearCloseTimer();
                                setFlyoutIndex(flyoutIndex);
                            }}
                        >
                            {flyoutItem.subItems.map((subItem, subIndex) => (
                                <div
                                    key={subIndex}
                                    className="client-action-card-flyout-item"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        subItem.onClick?.();
                                        setIsOpen(false);
                                        setFlyoutIndex(null);
                                    }}
                                >
                                    <span className="item-label">{subItem.label}</span>
                                    {subItemNumber && subItem.number != null && (
                                        <div className="item-notification-count-small">{subItem.number}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientActionCard;
